import dayjs, { Dayjs } from 'dayjs';
import { NodeResult } from '@/services/api.types';
import {
  MeasurementSeverity,
  MeasurementSeveritySort,
} from '@/utils/measurements/table-data-generator';
import { REDIRECT_SEARCH_PARAM_KEY } from '@/constants/system';
import { NodeResultAbstract } from '@/utils/measurements/node-results/node-result.abstract';
import { MultiChoiceResultClass } from '@/utils/measurements/node-results/multi-choice-result.class';
import { BloodPressureResultClass } from '@/utils/measurements/node-results/blood-pressure-result.class';
import { SingleResultClass } from '@/utils/measurements/node-results/single-result.class';

export const getStartTimeFromDate = (d: Date | Dayjs): Date => {
  const date = dayjs(d);
  return date
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)
    .toDate();
};

export const getEndTimeFromDate = (d: Date | Dayjs, isNow?: boolean): Date => {
  const date = dayjs(d);
  return date
    .set('hour', isNow ? date.get('hour') : 23)
    .set('minute', 59)
    .set('second', 59)
    .set('millisecond', 999)
    .toDate();
};

export const getTimestampFromDate = (d: Date): number | undefined => {
  if (!d) {
    return undefined;
  }
  return Math.floor(d.getTime() / 1000);
};

// Takes anything that names a group. Batch 007's contract row carries
// `organizations` (OrgRef: string id + name) where the legacy row carried
// `patient_groups` (PatientGroup: numeric id + department_id + name); only the
// name is read here, so the parameter is narrowed to that.
export const concatPatientGroup = (
  groups: { name: string }[] | undefined
): string => {
  if (groups) {
    return groups.map(g => g.name).join(', ');
  } else {
    return '-';
  }
};

// ⚠️ DELETED with PD-2956: `extractAdmissionDateFromMetadata` and
// `extractTreatmentIndicationFromMetadata`, plus the two
// `ADMISSION_*_METADATA_KEY` constants they matched on.
//
// They dug the admission date and the treatment indication out of a row's
// `metadatas` by literal Danish key — the legacy EAV shape ADR-A14 dropped as
// never delivered. No response has carried `metadatas` since the rebuild, so
// both helpers returned their `'-'` fallback on every call, on every tenant.
// Both values are now plain columns on `PatientSummary` (`admission_date` /
// `admission_reason`) and are read directly. Named here rather than silently
// removed because the harm was the helper existing at all: a lookup with a
// tidy fallback makes "the backend stopped sending this" and "this citizen has
// no admission date" indistinguishable at the call site.

// ⚠️ UPPERCASE only, and it stays that way. Its vocabulary is
// `MeasurementResult.severity` / `NodeResult.severity`, which api.types.ts
// documents at :947 and :1026 as the bare UPPER words `GREEN/YELLOW/ORANGE/RED`
// (`UNKNOWN` too). Its three remaining callers all read that field:
// measurement-header-cell.tsx, measurement-value-cell/value-result.tsx and
// patient-alarm-overview.tsx.
//
// A patient row's `last_alarm` is a DIFFERENT shape — lower-case bare words,
// produced by `severitySlugToBare` at the response boundary — and belongs to
// `mapBareSeverityToEnum` below. Widening this switch to accept both casings was
// considered and rejected for the reason patient-slug.ts states about its own
// tables: a table that accepts either shape can never show us the backend has
// drifted.
export const mapDBSeverityToEnum = (
  dbSeverity: string | undefined
): MeasurementSeverity => {
  switch (dbSeverity) {
    case 'GREEN':
      return MeasurementSeverity.Green;
    case 'RED':
      return MeasurementSeverity.Red;
    case 'BLUE':
      return MeasurementSeverity.Blue;
    case 'GRAY':
      return MeasurementSeverity.Gray;
    case 'YELLOW':
      return MeasurementSeverity.Yellow;
    case 'ORANGE':
      return MeasurementSeverity.Orange;
    case 'GREENCHECKED':
      return MeasurementSeverity.GreenCheckmark;
    default:
      return MeasurementSeverity.NoAlarm;
  }
};

// PD-2953. The patient-row counterpart of `mapDBSeverityToEnum`: `last_alarm`
// arrives as a LOWER-case bare word because `patientRowFromWire` already ran
// `severitySlugToBare` on it (`#Severity.Yellow` → `yellow`). Feeding that to the
// UPPERCASE switch above sent every pinned row down its `default` branch, so the
// pinned strip's status facet only ever offered "No Alarm" — while the card's own
// bell, which reads the bare value directly, showed the right colour.
//
// ⚠️ The RANGE of this function is deliberately narrow, and narrowing it is not
// laziness — it is a safety property. PinedPatientFilter rebuilds `statusFilters`
// wholesale from its `allStatus` list on every click, and PatientListPresentation
// only renders rows whose label survives in that list. A band produced here that
// the dropdown does not offer would make those patients VANISH from the strip the
// moment anyone touches the filter. So: only the four bands the dropdown offers,
// and everything else — '', undefined, 'unknown', and the non-wire 'gray' /
// 'blue' / 'greenchecked' — collapses to NoAlarm, which the dropdown does offer.
export const mapBareSeverityToEnum = (
  bareSeverity: string | undefined
): MeasurementSeverity => {
  switch ((bareSeverity ?? '').toLowerCase()) {
    case 'red':
      return MeasurementSeverity.Red;
    case 'orange':
      return MeasurementSeverity.Orange;
    case 'yellow':
      return MeasurementSeverity.Yellow;
    case 'green':
      return MeasurementSeverity.Green;
    default:
      return MeasurementSeverity.NoAlarm;
  }
};

export const calculateAgeFromBirth = (birthDateString: string): string => {
  const birthDate = dayjs(birthDateString);
  const now = dayjs();
  const diff = now.diff(birthDate, 'month');
  const monthInYear = 12;
  return Math.floor(diff / monthInYear).toString();
};

export const createNodeResultClassByNodeResultData = (
  nodeResult: NodeResult
): NodeResultAbstract => {
  switch (nodeResult.class) {
    case 'MultipleChoiceNodeResult':
      return new MultiChoiceResultClass(nodeResult);
    case 'MeasurementNodeResult':
    default:
      switch (nodeResult?.measurement_result?.class) {
        case 'BloodPressureResult':
          return new BloodPressureResultClass(nodeResult);
        case 'SingleResult':
        default:
          return new SingleResultClass(nodeResult);
      }
  }
};

export const getMostSeverAlarm = (
  severities: MeasurementSeverity[]
): MeasurementSeverity => {
  let result = MeasurementSeveritySort.length - 1;
  severities.forEach(s => {
    if (result === 0) {
      return;
    }
    result = Math.min(MeasurementSeveritySort.indexOf(s), result);
  });
  return MeasurementSeveritySort[result];
};

export const getRedirectQueryStringInCurrentLocation = (): string =>
  `${REDIRECT_SEARCH_PARAM_KEY}=${encodeURIComponent(window.location.href)}`;
